import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import FAQPanel from './FAQPanel';
import Navbar from './Navbar';
import AdPageLayout from './AdPageLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { AuthContext } from '../contexts/AuthContext';
import { translations } from '../translations';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f7f9;
`;

const FAQContainer = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  padding: 0 1rem;
  flex: 1;
  margin-bottom: 0;
  padding-bottom: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  color: #14464F;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-family: 'Josefin Sans', sans-serif;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
`;

const SearchContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  position: relative;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  padding-right: 3rem;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  color: #14464F;
  
  &:focus {
    outline: none;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.15);
  }

  &::placeholder {
    color: #14464F;
    opacity: 0.7;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #14464F;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

const ShowMoreButton = styled.button`
  background: none;
  border: 2px solid #14464F;
  color: #14464F;
  padding: 0.8rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  margin: 2rem auto 0;
  display: block;
  transition: all 0.3s ease;
  
  &:hover {
    background: #14464F;
    color: white;
  }
`;

const PanelsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
`;

const LoadingWrapper = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;



const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [displayCount, setDisplayCount] = useState(3);
  const { language } = useLanguage();
  const { user } = useContext(AuthContext);
  const t = translations[language].faq;
  const isPremium = user?.subscription_status === 'premium';



  const fetchFAQs = async (searchTerm) => {
    try {
      setLoading(true);
      setError(null);
      
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.get(`${apiUrl}/api/faqs`, {
        params: { 
          search: searchTerm,
          language
        }
      });
      
      setFaqs(response.data.faqs || []);
      setDisplayCount(3);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setError(error.message);
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = debounce((searchTerm) => {
    fetchFAQs(searchTerm);
  }, 300);

  useEffect(() => {
    fetchFAQs('');
  }, [language]);

  useEffect(() => {
    if (search !== undefined) {
      debouncedFetch(search);
    }
  }, [search]);

  const handleClearSearch = () => {
    setSearch('');
    fetchFAQs('');
  };

  const handleShowMore = () => {
    setDisplayCount(prevCount => prevCount + 3);
  };

  const visibleFaqs = faqs.slice(0, displayCount);
  const hasMoreFaqs = faqs.length > displayCount;

  return (
    <PageContainer>
      <Navbar />
      <AdPageLayout>
        <FAQContainer>
          <Header>
            <Title>{t.title}</Title>
            <Subtitle>{t.subtitle}</Subtitle>
          </Header>

          <SearchContainer>
            <SearchInput
              type="text"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <ClearButton onClick={handleClearSearch}>
                <FontAwesomeIcon icon={faTimes} />
              </ClearButton>
            )}
          </SearchContainer>

          {error && <NoResults>{t.error}{error}</NoResults>}
          
          {loading ? (
            <LoadingWrapper>{t.loading}</LoadingWrapper>
          ) : faqs.length === 0 ? (
            <NoResults>{t.noResults}</NoResults>
          ) : (
            <>
              <PanelsWrapper>
                {visibleFaqs.map((faq) => (
                  <FAQPanel
                    key={faq.id}
                    title={faq.title}
                    content={faq.description}
                  />
                ))}
              </PanelsWrapper>
              
              {hasMoreFaqs && (
                <ShowMoreButton onClick={handleShowMore}>
                  {t.showMore}
                </ShowMoreButton>
              )}
            </>
          )}
        </FAQContainer>
      </AdPageLayout>
    </PageContainer>
  );
};

export default FAQ;